// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type {
  AutoUpdateOptions,
  DetectOverflowOptions,
  FlipOptions,
  Middleware,
  Placement,
  ReferenceElement,
  ShiftOptions,
  Strategy,
} from '@floating-ui/dom'
import {
  autoUpdate,
  computePosition,
  flip,
  limitShift,
  offset,
  shift,
} from '@floating-ui/dom'
import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { OffsetOptions } from '../../helpers'
import {
  ignorePromise,
  propertyToPx,
  resolveTarget,
  roundByDPR,
} from '../../helpers'

let autoIncrementingId = 1

const isQdsButton = (
  element: globalThis.Element,
): element is HTMLQdsButtonElement =>
  element.tagName.toLowerCase() === 'qds-button'

/**
 * @slot Content to display in the dropdown.
 * @see https://quartz.se.com/build/components/dropdown
 */
@Component({
  tag: 'qds-dropdown',
  shadow: true,
  styleUrl: 'dropdown.css',
})
export class Dropdown implements ComponentInterface {
  /**
   * Options to pass to Floating UI's
   * [`autoUpdate()` function](https://floating-ui.com/docs/autoUpdate).
   *
   * @see https://floating-ui.com/docs/autoupdate#options
   */
  @Prop() public readonly autoUpdateOptions?: AutoUpdateOptions

  /**
   * Prevents the dropdown from being shown by user interaction. The dropdown
   * can still be displayed by calling the `show()` method.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * Options to pass to Floating UI's
   * [`flip` middleware](https://floating-ui.com/docs/flip).
   *
   * The [`crossAxis` option](https://floating-ui.com/docs/flip#crossaxis) is
   * set to the value of the `noShift` property by default for interoperability
   * with the [`shift` middleware](https://floating-ui.com/docs/shift).
   *
   * @see https://floating-ui.com/docs/flip#options
   */
  @Prop() public readonly flipOptions?: Partial<
    DetectOverflowOptions & FlipOptions
  >

  /**
   * Disable Floating UI's
   * [`flip` middleware](https://floating-ui.com/docs/flip).
   */
  @Prop() public readonly noFlip: boolean = false

  /**
   * Disable Floating UI's
   * [`shift` middleware](https://floating-ui.com/docs/shift).
   */
  @Prop() public readonly noShift: boolean = false

  /**
   * Options to pass to Floating UI's
   * [`offset` middleware](https://floating-ui.com/docs/offset).
   *
   * The [`mainAxis` option](https://floating-ui.com/docs/offset#mainaxis) is
   * not supported.
   *
   * @see https://floating-ui.com/docs/offset#options
   */
  @Prop() public readonly offsetOptions?: OffsetOptions

  /**
   * Where to place the dropdown relative to the `target`.
   *
   * @see https://floating-ui.com/docs/tutorial#placements
   */
  @Prop() public readonly placement: Placement = 'bottom-start'

  /**
   * Options to pass to Floating UI's
   * [`shift` middleware](https://floating-ui.com/docs/shift).
   *
   * @see https://floating-ui.com/docs/shift#options
   */
  @Prop() public readonly shiftOptions?: Partial<
    DetectOverflowOptions & ShiftOptions
  >

  /**
   * The CSS position strategy to use.
   *
   * @see https://floating-ui.com/docs/computeposition#strategy
   */
  @Prop() public readonly strategy?: Strategy

  /**
   * The trigger element that will trigger the dropdown. The target specified
   * can be any of the following:
   *
   * - A CSS selector string
   * - A reference to an [`Element`][] or [Virtual Element][]
   * - A function returning a reference to an [`Element`][],
   * [Virtual Element][], or a CSS selector string
   *
   * [`Element`]: https://developer.mozilla.org/docs/Web/API/Element
   * [Virtual Element]: https://floating-ui.com/docs/virtual-elements
   */
  @Prop() public readonly target!:
    | ReferenceElement
    | string
    | (() => ReferenceElement | string)

  /**
   * Emitted when the user instructs the browser that they wish to dismiss the
   * current open dropdown by pressing the `Esc` key.
   *
   * When a dropdown is dismissed with the `Esc` key, both the `qdsCancel` and
   * `qdsClose` events are emitted.
   */
  @Event({ eventName: 'qdsCancel', bubbles: false })
  private readonly cancelEmitter!: EventEmitter<void>

  /**
   * Emitted when the dropdown is closed.
   */
  @Event({ eventName: 'qdsClose', bubbles: false, cancelable: false })
  private readonly closeEmitter!: EventEmitter<void>

  @Element() private readonly host!: HTMLElement

  @State() private hostAriaLabelledBy?: string

  @State() private hostDisplay?: string

  @State() private hostPosition?: string

  @State() private hostTransform?: string

  #cleanup?: () => void

  #middleware?: Middleware[]

  #referenceElement?: ReferenceElement

  #open = false

  /**
   * Update the dropdown's middleware and position.
   */
  @Method()
  public async update(): Promise<void> {
    await this.updateMiddleware()
  }

  @Listen('click', { target: 'document' })
  protected handleMouseDown(event: MouseEvent): void {
    if (!this.#open) return

    const composedPath = event.composedPath()
    const withinBounds =
      composedPath.includes(this.host) ||
      (this.#referenceElement instanceof globalThis.Element &&
        composedPath.includes(this.#referenceElement))
    if (!withinBounds) this.close()
  }

  @Watch('target')
  protected async updateReferenceElement(): Promise<void> {
    this.#disconnectReferenceElement()

    this.#referenceElement = resolveTarget(this.target)

    if (!(this.#referenceElement instanceof globalThis.Element)) return

    this.#referenceElement.setAttribute('aria-expanded', 'false')
    this.#referenceElement.addEventListener('click', this.#onReferenceClick)
    this.#referenceElement.addEventListener('focusout', this.#onFocusout)
    this.#referenceElement.addEventListener('keydown', this.#onKeydown)

    if (this.host.role === 'menu') {
      this.#referenceElement.id ||= `qds-dropdown-trigger-${autoIncrementingId}`
      this.hostAriaLabelledBy = this.#referenceElement.id
    }
    await this.updatePosition()
  }

  @Watch('flipOptions')
  @Watch('noFlip')
  @Watch('noShift')
  @Watch('offsetOptions')
  @Watch('shiftOptions')
  protected async updateMiddleware(): Promise<void> {
    this.#middleware = [
      offset({
        ...this.offsetOptions,
        mainAxis: propertyToPx(this.host, '--qds-accessory-gap-floating') ?? 0,
      }),
      !this.noFlip &&
        flip({
          crossAxis: this.noShift,
          ...this.flipOptions,
        }),
      !this.noShift &&
        shift({
          limiter: limitShift(),
          ...this.shiftOptions,
        }),
    ].filter((middleware): middleware is Middleware => middleware !== false)
    await this.updatePosition()
  }

  @Watch('placement')
  @Watch('strategy')
  protected async updatePosition(): Promise<void> {
    if (!this.#referenceElement) return

    const { x, y, strategy } = await computePosition(
      this.#referenceElement,
      this.host,
      {
        middleware: this.#middleware,
        placement: this.placement,
        strategy: this.strategy,
      },
    )
    this.hostPosition = strategy
    this.hostTransform = `translate(${roundByDPR(x)}px,${roundByDPR(y)}px)`
  }

  @Watch('autoUpdateOptions')
  protected startAutoUpdate(): void {
    if (!this.#referenceElement) return

    if (this.#cleanup) this.#cleanup()
    this.#cleanup = autoUpdate(
      this.#referenceElement,
      this.host,
      () => {
        ignorePromise(this.updatePosition())
      },
      this.autoUpdateOptions,
    )
  }

  public async componentWillLoad(): Promise<void> {
    this.hostDisplay = 'none'
    this.host.role ??= 'menu'
    this.host.id ||= `qds-dropdown-${autoIncrementingId}`
    await this.updateReferenceElement()
    autoIncrementingId += 1
    await this.updateMiddleware()
  }

  public disconnectedCallback(): void {
    this.#disconnectReferenceElement()
  }

  public render() {
    return (
      <Host
        aria-labelledby={this.hostAriaLabelledBy}
        onKeyDown={this.#onKeydown}
        onFocusout={this.#onFocusout}
        style={{
          display: this.hostDisplay,
          position: this.hostPosition,
          transform: this.hostTransform,
        }}
      >
        <slot />
      </Host>
    )
  }

  public show(): void {
    if (this.#open) return

    if (this.#referenceElement instanceof globalThis.Element) {
      this.#referenceElement.setAttribute('aria-expanded', 'true')
      this.#referenceElement.setAttribute('aria-controls', this.host.id)

      if (
        isQdsButton(this.#referenceElement) &&
        this.#referenceElement.action === 'dropdown'
      )
        this.#referenceElement.action = 'dropdown-close'
    }

    this.#open = true
    this.hostDisplay = undefined
    this.startAutoUpdate()
  }

  public close(): void {
    if (!this.#open) return

    if (this.#referenceElement instanceof globalThis.Element) {
      this.#referenceElement.setAttribute('aria-expanded', 'false')
      this.#referenceElement.removeAttribute('aria-controls')

      if (
        isQdsButton(this.#referenceElement) &&
        this.#referenceElement.action === 'dropdown-close'
      )
        this.#referenceElement.action = 'dropdown'
    }

    this.#open = false
    this.hostDisplay = 'none'
    if (this.#cleanup) this.#cleanup()

    this.closeEmitter.emit()
  }

  readonly #onFocusout = (event: Event): void => {
    if (!(event instanceof FocusEvent)) return
    const { relatedTarget } = event

    if (
      relatedTarget instanceof globalThis.Element &&
      !this.host.contains(relatedTarget) &&
      this.#referenceElement !== relatedTarget
    )
      this.close()
  }

  readonly #onKeydown = (event: Event): void => {
    if (
      !(event instanceof KeyboardEvent) ||
      event.key !== 'Escape' ||
      (event.target !== this.#referenceElement &&
        event.target instanceof globalThis.Element &&
        event.target.closest('qds-dropdown') !== this.host)
    )
      return

    const cancelEvent = this.cancelEmitter.emit()
    if (cancelEvent.defaultPrevented) return

    this.close()
    if (
      this.#referenceElement instanceof globalThis.HTMLElement ||
      this.#referenceElement instanceof globalThis.SVGElement
    )
      this.#referenceElement.focus()
  }

  readonly #onReferenceClick = (): void => {
    if (this.disabled) return

    if (this.#open) this.close()
    else this.show()
  }

  #disconnectReferenceElement(): void {
    this.hostAriaLabelledBy = undefined

    if (!(this.#referenceElement instanceof globalThis.Element)) return

    this.#referenceElement.removeEventListener('focusout', this.#onFocusout)
    this.#referenceElement.removeEventListener('keydown', this.#onKeydown)
    this.#referenceElement.removeAttribute('aria-expanded')
    this.#referenceElement.removeAttribute('aria-controls')
  }
}
